/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  memo,
  useMemo,
  useEffect,
  useState,
} from 'react';
import styled from 'styled-components/macro';
import { matchPath, Prompt } from 'react-router-dom'
import { useRouterContext } from 'hooks';
import {
  Layout, PageMenu, Error,
} from 'components/organisms';
import { ModalConfirm } from 'components/molecules'
import { PageCreateLanguageModal, ErrorBoundaryEditor, ErrorBoundary } from 'containers'

import { useEditorContext, actions, status } from 'context/EditorContext';
import { useDispatch, useSelector } from 'react-redux'
import { deleteDraft } from 'redux/draftsSlice'

import { withTheme } from 'styled-components';
import { useQueryContent } from 'services/requests';
import { isAfter } from 'date-fns'
import { generateEditorPath, generateInitialPageData } from 'services/generators';
import { injectIntl } from 'react-intl'
import { intlType } from 'types'
import { useLastLocation } from 'react-router-last-location';

import { computeHasModules } from 'modules/utils'
import { isPathEditor } from 'services/helpers';
import EditorPage from './EditorPage';
import EditorModule from './EditorModule';
import EditorSettings from './EditorSettings';
import EditorHistory from './EditorHistory';
import EditorDiff from './EditorDiff';
import EditorJson from './EditorJson';
import EditorAddModule from './EditorAddModule';
import EditorCopyModules from './EditorCopyModules';
import EditorPasteModules from './EditorPasteModule';
import EditorNoContent from './EditorNoContent';
import EditorPermissions from './EditorPermissions';

const Editor = memo(
  ({ intl }) => {
    const {
      match: {
        params: {
          pageId, moduleId, lang, subweb
        },
      },
      location: { pathname },
      history,
    } = useRouterContext();

    const [{
      pageLiveData,
      pageOtherLanguageData,
      pageSavedData,
      pageEdited,
      pageTriggerRefetch,
      pageStatus,
    }, dispatch] = useEditorContext();
    const { pages: draftPages } = useSelector(
      (state) => state.drafts
    )
    const [newLanguageModal, setNewLanguageModal] = useState({
      isOpen: false,
      newLanguage: null,
    })
    const lastLocation = useLastLocation();
    const dispatchRedux = useDispatch()
    const pageDraft = useMemo(() => draftPages[pageId], [draftPages, pageId])

    /*
      handling of incorrect draft language
      opens up modal to ask if should restore or delete
    */
    const [isOpenIncorrectDraftLanguage, setIsOpenIncorrectDraftLanguage] = useState(false)
    const toggleIncorrectDraftLanguage = () => {
      setIsOpenIncorrectDraftLanguage(!isOpenIncorrectDraftLanguage)
    }

    const handleIncorrectDraftLanguage = (shouldRestore) => {
      // redirect to draft language or delete draft
      if (shouldRestore) history.push(generateEditorPath({ pageId, subweb, lang: pageDraft.lang }))
      else dispatchRedux(deleteDraft(pageId))
      toggleIncorrectDraftLanguage()
    }

    useEffect(() => {
      if (pageDraft && pageDraft.lang !== lang) {
        setIsOpenIncorrectDraftLanguage(true)
      }
    }, [pageDraft, lang])
    const isModule = useMemo(() => {
      // find if module exists in page
      if (!(pageId && moduleId && pageLiveData && pageLiveData.length > 0)) return false
      const [moduleName, moduleIndex] = moduleId.split('-')
      const moduleInPage = pageLiveData[moduleIndex]
      return moduleInPage && moduleInPage.name === moduleName
    }, [pageId, moduleId, pageLiveData]);

    // initial page query
    const {
      response: pageData,
      loading: pageLoading,
      error: pageError,
      reFetch: queryPage,
    } = useQueryContent({ id: pageId, subweb, language: lang });

    useEffect(() => {
      queryPage();
    }, [pageId, lang, pageTriggerRefetch]);

    // update editor context page loading from query
    useEffect(() => {
      dispatch({
        type: actions.PAGE_DATA_LOADING,
        data: pageLoading,
      });
    }, [pageLoading])

    // compute pageTitle from SEO and dispatch to context
    const pageTitleComputed = useMemo(() => {
      if (!pageLiveData) return 'Sem título';

      // parse title from seo data
      const seoData = pageLiveData.find(data => data.name === 'SEO')
      if (seoData) {
        const { title } = seoData;
        const titleLang = (title && title[lang]) ? title[lang] : null;
        if (titleLang) return titleLang;
        if (typeof title === 'string') return title;
      }

      // find first Article and get the title from it when SEO is null
      const article = pageLiveData.find(data => data.name === 'Article')
      if (article && article.data) {
        const { data: articleData } = article
        const articleDataLang = articleData[lang]
        if (articleDataLang && articleDataLang.title) return articleDataLang.title
        if (articleData && articleData.title) return articleData.title
      }
      // fallsback to page id
      return pageId;
    }, [pageLiveData, pageId, lang]);

    // check if is an empty page
    const hasModules = useMemo(() => computeHasModules(pageLiveData), [pageLiveData])
    const hasOtherLanguage = useMemo(() => computeHasModules(pageOtherLanguageData), [pageLiveData])


    const handleToggleLanguageModal = () => {
      setNewLanguageModal(
        {
          ...newLanguageModal,
          isOpen: !newLanguageModal.isOpen
        }
      )
    }

    useEffect(() => {
      dispatch({
        type: actions.PAGE_TITLE_UPDATE,
        data: pageTitleComputed,
      });
    }, [pageTitleComputed]);

    // handles page load
    useEffect(() => {
      if (pageLoading || pageError || !pageData) return;

      // 204 is returned when a version of the page doenst exist (normally language version)
      // this opens a popup asking to create the version of the page
      if (pageData && pageData.status === 204) {
        setNewLanguageModal({
          isOpen: true,
          newLanguage: lang,
        })
        // dummy page with status none
        const dummyPageContent = generateInitialPageData({ title: 'Título por definir' })
        dispatch({
          type: actions.PAGE_ADD,
          data: {
            liveData: dummyPageContent,
            savedData: dummyPageContent,
            status: status.none,
          },
        });
        return;
      }
      // remove null elements from pageData (bug from the api)
      if (pageData && pageData.data && pageData.data.pageContent) {
        const page = pageData.data.pageContent.filter(el => el !== null)
        dispatch({
          type: actions.PAGE_ADD,
          data: {
            liveData: (pageDraft && pageDraft.lang === lang) ? pageDraft.content : page,
            savedData: page,
            status: (pageDraft && pageDraft.lang === lang) ? status.local : pageData.data.state,
            slug: pageData.data.slug,
            otherLanguageContent: pageData.data.otherLanguageContent
          },
        });
      }
    }, [pageData, pageLoading, pageError]);

    const { params: { subEditor } } = useMemo(() => {
      const match = matchPath(pathname, {
        path: '/:subweb/editor/:lang/page/:pageId/:subEditor',
        exact: true,
      });
      return (match && match.isExact) ? match : { params: { subEditor: '' } };
    }, [pathname]);

    const isDiffPage = subEditor === 'diff'
    const isJsonPage = subEditor === 'json'
    const isHistoryPage = subEditor === 'history'
    const isPermissionsPage = subEditor === 'permissions'

    const layoutModifiers = useMemo(() => {
      const pre = ['stretch', 'darker', 'relative']
      return [...pre, (isDiffPage || isJsonPage) ? 'editorNoMenu' : 'editor']
    }, [isDiffPage, isJsonPage])

    // sets current module being edited
    useEffect(() => {
      if (!isModule) {
        dispatch({
          type: actions.SET_MODULE_EDITED,
          data: {
            key: null,
            name: null,
          },
        });
      } else {
        const moduleName = moduleId.split('-')[0]
        dispatch({
          type: actions.SET_MODULE_EDITED,
          data: {
            key: moduleId,
            name: moduleName,
          },
        });
      }

      return () => {
        dispatch({
          type: actions.SET_MODULE_EDITED,
          data: {
            key: null,
            name: null,
          },
        });
      }
    }, [isModule])

    // handling of outdated draft, warns user
    const isOutdated = useMemo(() => {
      if (!pageLiveData || !pageSavedData) return false
      const seoDB = pageSavedData.find(organism => organism.name === 'SEO')
      const seoLive = pageLiveData.find(organism => organism.name === 'SEO')
      // means it hasnt been edited before
      if (!seoDB || !seoDB.lastModified) return false
      if (!seoLive || !seoLive.lastModified) return false

      // check if lastModified from database is after lastModified from local storage
      const lastModifiedDB = new Date(seoDB.lastModified)
      const lastModifiedLive = new Date(seoLive.lastModified)
      return isAfter(lastModifiedDB, lastModifiedLive)
    }, [pageLiveData, pageSavedData])

    /* handling of outdated drafts popup */
    const [isOpenOutdatedModal, setIsOpenOutdatedModal] = useState(false)
    useEffect(() => {
      setIsOpenOutdatedModal(isOutdated)
    }, [isOutdated])
    const toggleOutdatedModal = () => setIsOpenOutdatedModal(!isOpenOutdatedModal)

    const handleUpdate = (shouldUpdatePage) => {
      if (shouldUpdatePage) {
        const page = pageData.data.pageContent.filter(el => el !== null)
        dispatch({
          type: actions.PAGE_ADD,
          data: {
            liveData: page,
            savedData: page,
            status: status.published,
            otherLanguageContent: pageData.data.otherLanguageContent
          },
        });
        dispatchRedux(deleteDraft(pageId))
        history.push(generateEditorPath({ pageId, subweb }))
      }
      toggleOutdatedModal()
    }
    /* end of handling of outdated draft */

    // alert user before page change
    // ignores if next page is in the editor or gather
    // only shows if page status is local (has been edited but not saved)
    const handleRouteChange = ({ pathname: next }) => {
      if ((isPathEditor(next) || next === '/gather') || pageStatus !== status.local) {
        return true
      }
      // ignore
      return 'Não gravou as suas edições, pretende sair?'
    }

    const renderPopups = () => (
      <>
        <PageCreateLanguageModal
          isOpen={newLanguageModal.isOpen}
          newLanguage={newLanguageModal.newLanguage}
          toggle={handleToggleLanguageModal}
        />
        <ModalConfirm
          isOpen={isOpenOutdatedModal}
          toggle={toggleOutdatedModal}
          onChange={handleUpdate}
          title="Versão desatualizada"
          message="Alguém editou esta página depois de si, pretende atualizar?"
          textConfirm="atualizar"
          textDiscard="cancelar"
        />
        <ModalConfirm
          isOpen={isOpenIncorrectDraftLanguage}
          toggle={toggleIncorrectDraftLanguage}
          onChange={handleIncorrectDraftLanguage}
          title="Edições por gravar"
          message={`Deseja recuperar as alterações realizadas na versão ${intl.formatMessage({ id: `lang.${(pageDraft && pageDraft.lang) || 'pt'}.feminim` })}?`}
          textConfirm="recuperar"
          textDiscard="cancelar"
        />
      </>
    )

    const renderPage = () => (
      <ErrorBoundaryEditor subweb={subweb} pageId={pageId}>
        <EditorPage moduleToFocus={moduleId} />
        <Wrapper>
          <div>
            <EditorAddModule hasModules={hasModules} />
            <EditorCopyModules
              hasModules={hasModules}
              hasOtherLanguage={hasOtherLanguage}
              otherLanguageContent={pageOtherLanguageData}
            />
            <EditorPasteModules />
          </div>
        </Wrapper>
      </ErrorBoundaryEditor>
    )

    const renderView = () => {
      if (
        pageStatus === status.none
        && (pageLiveData && pageLiveData.length > 0)
      ) return <EditorNoContent />
      if (!pageLoading && pageLiveData) {
        if (isDiffPage) {
          return (
            <EditorDiff
              newCode={JSON.stringify(pageLiveData, null, 2)}
              oldCode={JSON.stringify(pageSavedData, null, 2)}
              hasEdited={pageEdited}
            />
          )
        }
        if (isJsonPage) return <EditorJson />
        // only page
        return (
          <SideBySide>
            <SideBySide.Start>
              {renderPage()}
            </SideBySide.Start>
            <SideBySide.End>
              {isModule && <EditorModule disableInline />}
              {isHistoryPage && <EditorHistory />}
              {isPermissionsPage && <EditorPermissions />}
              {!isModule && !isHistoryPage && !isPermissionsPage && <EditorSettings />}
            </SideBySide.End>
          </SideBySide>
        )
      }
      return null
    }

    // scroll to top if last page is not the editor
    // or: is json / diff
    const shouldScrollToTop = useMemo(() => {
      if (lastLocation && lastLocation.pathname) {
        const { pathname: prevPathname } = lastLocation
        const isEditor = prevPathname.indexOf('editor') !== -1
        const isJson = prevPathname.indexOf('json') !== -1
        const isDiff = prevPathname.indexOf('diff') !== -1
        return !isEditor || isJson || isDiff
      } return true
    }, [lastLocation])

    if (pageError) {
      const message = pageError.message || 'Erro a obter página.'
      return (
        <ErrorWrapper>
          <Error message={message} />
        </ErrorWrapper>
      )
    }

    return (
      <>
        {renderPopups()}
        <Layout modifiers={layoutModifiers} scrollToTopOnMount={shouldScrollToTop}>
          <ErrorBoundary>
            <Prompt message={handleRouteChange} />
            <PageMenuWrapper>
              <PageMenu loading={pageLoading} pageId={pageId} />
            </PageMenuWrapper>
            <View>
              {renderView()}
            </View>
          </ErrorBoundary>
        </Layout>
      </>
    );
  });

const PageMenuWrapper = styled.div`
  position: sticky;
  z-index: ${props => props.theme.values.zIndex.l};
  top: 0;
  border-bottom: ${props => props.theme.values.borderSizeM} solid
    ${props => props.theme.colors.borderLighter};
`;

const View = styled.div`
`;

const SideBySideAlt = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`

SideBySideAlt.Start = styled.div`
  top: 50px;
  position: sticky;
  width: ${props => props.theme.components.editor.sideMenuWidth};
  height: calc(100vh - 90px);
  background-color: ${props => props.theme.colors.bgLighterS};
  border-right: ${props => props.theme.values.borderSizeM} solid
    ${props => props.theme.colors.borderLighter};
`

SideBySideAlt.End = styled.div`
  width: ${props => `calc(100% - ${props.theme.components.editor.sideMenuWidth})`};
`

const SideBySide = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
`

SideBySide.Start = styled.div`
  width: ${props => `calc(100% - ${props.theme.components.editor.sideMenuWidth})`};
`

SideBySide.End = styled.div`
  top: 50px;
  position: sticky;
  width: ${props => props.theme.components.editor.sideMenuWidth};
  height: calc(100vh - 90px);
  background-color: ${props => props.theme.colors.bgLighterS};
  border-left: ${props => props.theme.values.borderSizeM} solid
    ${props => props.theme.colors.borderLighter};
`

const ErrorWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  padding: ${props => props.theme.spacing.xl6} 0;
  display: flex;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
`

Editor.propTypes = {
  intl: intlType.isRequired,
}


export default withTheme(injectIntl(Editor));
